/* eslint-disable max-statements */
import { useCallback, useContext, useMemo } from 'react';
import { debounce } from 'lodash-es';
import type { MouseEvent } from 'react';
import { isElementVisibleCarousel } from '../../utils/helpers.js';
import { DSTabsContext } from '../../DSTabsCTX.js';

interface UseCarouselCallbacksPropsT {
  getVisibleItemsOffset: () => number;
  recalcChevrons: () => void;
}
interface UseCarouselCallbacksReturnTypeT {
  leftButtonOnClick: () => void;
  rightButtonOnClick: () => void;
  handleOnClick: (e: React.MouseEvent) => void;
  handleOnScroll: (e: React.UIEvent) => void;
}

export const useCarouselCallbacks = ({
  getVisibleItemsOffset,
  recalcChevrons,
}: UseCarouselCallbacksPropsT): UseCarouselCallbacksReturnTypeT => {
  const { focusableTabsRef, carouselOnlyListRef } = useContext(DSTabsContext);

  const rightButtonOnClick = useCallback(() => {
    if (carouselOnlyListRef.current) {
      carouselOnlyListRef.current.scrollLeft += getVisibleItemsOffset();
    }
  }, [getVisibleItemsOffset, carouselOnlyListRef]);

  const leftButtonOnClick = useCallback(() => {
    if (carouselOnlyListRef.current) {
      carouselOnlyListRef.current.scrollLeft -= getVisibleItemsOffset();
    }
  }, [getVisibleItemsOffset, carouselOnlyListRef]);

  /** [PUI-15772] - Tabs Pre Refactor
   * Scroll handler for carousel. It:
   * 1. Recalculates chevron visibility
   * 2. Updates indicator style
   * 3. May cause rerenders on every scroll event
   */
  const handleOnScroll = useMemo(() => debounce(recalcChevrons, 200), [recalcChevrons]);

  /** [PUI-15772] - Tabs Pre Refactor
   * Click handler for carousel tabs. It:
   * 1. Checks if clicked element is a tab
   * 2. Calculates scroll position to center the tab
   * 3. Updates scroll position with magic number adjustments
   * 4. May cause rerenders due to scroll position updates
   */
  const handleOnClick = useCallback(
    ({ target }: MouseEvent) => {
      if (focusableTabsRef.current && (target as HTMLButtonElement).getAttribute('role') === 'tab') {
        const tabList = carouselOnlyListRef.current;
        const tabButton = target as HTMLButtonElement;
        const tabButtonDimensions = tabButton.getBoundingClientRect();
        const tabListDimensions = tabList?.getBoundingClientRect();
        const tabButtonIndex = tabButton.dataset.index as string;
        const lastItemIndex = focusableTabsRef.current.length.toString();
        const magicNumber = 34;

        const firstOrLastItem = tabButtonIndex === '0' || tabButtonIndex === lastItemIndex;

        if (
          !isElementVisibleCarousel(tabButton, tabList, firstOrLastItem) &&
          tabListDimensions &&
          carouselOnlyListRef.current
        ) {
          const containerWidth = tabListDimensions.width;
          const halfContainer = containerWidth / 2;
          const elementScrollPosition = tabButtonDimensions.left - tabListDimensions.left;
          const positionInContainer = elementScrollPosition % containerWidth;
          const width = tabButton.offsetWidth + magicNumber;

          if (positionInContainer > halfContainer) {
            carouselOnlyListRef.current.scrollLeft += width + positionInContainer - containerWidth;
          } else {
            carouselOnlyListRef.current.scrollLeft += positionInContainer - magicNumber;
          }
        }
      }
    },
    [focusableTabsRef, carouselOnlyListRef],
  );

  return {
    handleOnClick,
    rightButtonOnClick,
    leftButtonOnClick,
    handleOnScroll,
  };
};
